import React, { SetStateAction } from 'react'
import { Button } from '../../../../../common/components/button/Button'
import { Input } from '../../../../../common/components/input/Input'
import { Textarea } from '../../../../../common/components/textarea/Textarea'
import { PlaceInterface } from '../../../../../common/types/interfaces'
import classes from './Form.module.sass'
import { useForm } from './hooks/useForm'
import { Toggle } from '../../../../../common/components/toggle/Toggle'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../../store/slices/currentClusterSlice'
import { ClusterTypes } from '../../../../../common/types/enums'
import { SearchSelect } from '../../../../../common/components/searchSelect/SearchSelect'
import { SearchGroupSelect } from '../../../../../common/components/searchGroupSelect/SearchGroupSelect'

type Props = {
  edit: boolean
  data: PlaceInterface
  setData: React.Dispatch<SetStateAction<PlaceInterface>>
  getList: () => void
  setModalActive: React.Dispatch<SetStateAction<boolean>>
  el?: PlaceInterface
}

export const Form = ({ edit, data, setData, getList, setModalActive, el }: Props) => {
  const {
    formAction,
    inputHandler,
    setDirty,
    getValidationResult,
    disabled,
    view,
    setView,
    createStoreSelectList,
    createItemSelectList,
    createCategorySelectList,
  } = useForm(data, setData, edit, getList, setModalActive, el)

  const currentCluster = useSelector(selectCurrentCluster)
  const isHotel = currentCluster?.type === ClusterTypes.HOTEL

  return (
    <div className={classes.container}>
      <div className={classes.bookMarks}>
        <div
          className={view === 'main' ? classes.titleActive : classes.title}
          onClick={() => setView('main')}
        >
          Основное
        </div>
        <div
          className={view === 'booking' ? classes.titleActive : classes.title}
          onClick={() => setView('booking')}
        >
          Бронирование
        </div>
        <div
          className={view === 'availability' ? classes.titleActive : classes.title}
          onClick={() => setView('availability')}
        >
          Доступность
        </div>
      </div>

      {/* Вкладка Основное */}
      {view === 'main' && (
        <div className={classes.tabContent}>
          <SearchSelect
            onBlur={setDirty}
            fieldName="storeId"
            options={createStoreSelectList()}
            value={data.storeId}
            onChangeHandler={inputHandler}
            type="text"
            placeholder="Выберите точку"
            itemValidationResult={getValidationResult('storeId')}
          />
          <Input
            type={'text'}
            fieldName={'name'}
            value={data.name}
            placeHolder={'Название места'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('name')}
            onBlur={setDirty}
          />
          <Textarea
            type={'textarea'}
            fieldName={'description'}
            value={data.description || ''}
            placeHolder="Описание места"
            rows={5}
            cols={20}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('description')}
            onBlur={setDirty}
          />
          <Input
            label="Номер места"
            type={'text'}
            fieldName={'placeNumber'}
            value={data.placeNumber || ''}
            placeHolder={'Номер места (опционально)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('placeNumber')}
            onBlur={setDirty}
          />
          <Input
            label="Порядок сортировки"
            type={'text'}
            fieldName={'sortOrder'}
            value={data.sortOrder}
            placeHolder={'Порядок сортировки'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('sortOrder')}
            onBlur={setDirty}
          />
          <div className={classes.toggleContainer}>
            <Toggle
              id="isActive"
              onChange={() => {
                setData((prev) => ({ ...prev, isActive: !prev.isActive }))
              }}
              checked={data.isActive}
            />
            <div>Активно</div>
          </div>
        </div>
      )}

      {/* Вкладка Бронирование */}
      {view === 'booking' && (
        <div className={classes.tabContent}>
          <Input
            label="Длительность слота (мин)"
            type={'text'}
            fieldName={'slotDurationMinutes'}
            value={data.slotDurationMinutes || ''}
            placeHolder={'Длительность слота (мин)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('slotDurationMinutes')}
            onBlur={setDirty}
          />
          <Input
            label="Шаг сетки (мин)"
            type={'text'}
            fieldName={'slotIntervalMinutes'}
            value={data.slotIntervalMinutes || ''}
            placeHolder={'Шаг сетки (мин)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('slotIntervalMinutes')}
            onBlur={setDirty}
          />
          <Input
            label="Перерыв между слотами (мин)"
            type={'text'}
            fieldName={'breakBetweenSlotsMinutes'}
            value={data.breakBetweenSlotsMinutes || ''}
            placeHolder={'Перерыв между слотами (мин)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('breakBetweenSlotsMinutes')}
            onBlur={setDirty}
          />
          <Input
            label="Мин. длительность брони (мин)"
            type={'text'}
            fieldName={'minBookingDurationMinutes'}
            value={data.minBookingDurationMinutes || ''}
            placeHolder={'Мин. длительность брони (мин)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('minBookingDurationMinutes')}
            onBlur={setDirty}
          />
          <Input
            label="Макс. длительность брони (мин)"
            type={'text'}
            fieldName={'maxBookingDurationMinutes'}
            value={data.maxBookingDurationMinutes || ''}
            placeHolder={'Макс. длительность брони (мин)'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('maxBookingDurationMinutes')}
            onBlur={setDirty}
          />
          <Input
            label="Отмена за X минут до слота"
            type={'text'}
            fieldName={'cancellationDeadlineMinutes'}
            value={data.cancellationDeadlineMinutes || ''}
            placeHolder={'Отмена за X минут до слота'}
            onChangeHandler={inputHandler}
            itemValidationResult={getValidationResult('cancellationDeadlineMinutes')}
            onBlur={setDirty}
          />
          {isHotel && (
            <>
              <Input
                type={'text'}
                fieldName={'checkinTime'}
                value={data.checkinTime || ''}
                placeHolder={'Время заезда (ЧЧ:ММ)'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('checkinTime')}
                onBlur={setDirty}
              />
              <Input
                type={'text'}
                fieldName={'checkoutTime'}
                value={data.checkoutTime || ''}
                placeHolder={'Время выезда (ЧЧ:ММ)'}
                onChangeHandler={inputHandler}
                itemValidationResult={getValidationResult('checkoutTime')}
                onBlur={setDirty}
              />
            </>
          )}
        </div>
      )}

      {/* Вкладка Доступность */}
      {view === 'availability' && (
        <div className={classes.tabContent}>
          <SearchGroupSelect
            onBlur={setDirty}
            fieldName="itemIds"
            options={createItemSelectList()}
            value={data.itemIds || []}
            onChangeHandler={(value) => {
              if (value === '' || value === undefined) return
              const currentIds = data.itemIds || []
              setData((prev) => ({ ...prev, itemIds: [...currentIds, Number(value)] }))
            }}
            onRemoveHandler={(value) => {
              const currentIds = data.itemIds || []
              setData((prev) => ({
                ...prev,
                itemIds: currentIds.filter((id) => id !== Number(value)),
              }))
            }}
            type="array"
            placeholder="Выберите товары"
            itemValidationResult={getValidationResult('itemIds')}
          />

          <SearchGroupSelect
            onBlur={setDirty}
            fieldName="categoryIds"
            options={createCategorySelectList()}
            value={data.categoryIds || []}
            onChangeHandler={(value) => {
              if (value === '' || value === undefined) return
              const currentIds = data.categoryIds || []
              setData((prev) => ({ ...prev, categoryIds: [...currentIds, Number(value)] }))
            }}
            onRemoveHandler={(value) => {
              const currentIds = data.categoryIds || []
              setData((prev) => ({
                ...prev,
                categoryIds: currentIds.filter((id) => id !== Number(value)),
              }))
            }}
            type="array"
            placeholder="Выберите категории"
            itemValidationResult={getValidationResult('categoryIds')}
          />
        </div>
      )}

      <Button text={edit ? 'Сохранить' : 'Добавить'} onClick={formAction} disabled={disabled} />
    </div>
  )
}
